import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  addParamToUrl,
  addParamToUrlAndRefresh,
} from '@innovamat/radiance-utils';
import { useRegionalConfigVariables } from '@innovamat/regional-config-variables';

export const DATA_CENTER_REGIONS = {
  USA: 'USA',
  EUROPE: 'Europe',
  LATAM: 'LATAM',
} as const;

type DataCenterRegion =
  (typeof DATA_CENTER_REGIONS)[keyof typeof DATA_CENTER_REGIONS];

type UseDataCenterProps = {
  handleIsUsa?: (isUsa: boolean) => void;
};

type UseDataCenterReturn = {
  dataCenterRegion: DataCenterRegion | null;
  selectedLanguage: string;
  setDataCenterRegionAndChangeUrl: (region: string, language?: string) => void;
  handleSelectLanguage: (languageLocale: string) => Promise<void>;
};

export const useDataCenter = ({
  handleIsUsa,
}: UseDataCenterProps = {}): UseDataCenterReturn => {
  const { i18n } = useTranslation();
  const { lng } = useParams<{ lng: string }>();

  const params = new URLSearchParams(document.location.search);
  const selectedLanguage =
    params.get('l') || params.get('language') || lng || i18n.language;

  const {
    geoLocation: { isUS, country },
  } = useRegionalConfigVariables();
  const dataCenter =
    params.get('dc')?.toLowerCase() === 'usa'
      ? 'USA'
      : params.get('dc')?.toLowerCase() === 'europe'
      ? 'Europe'
      : params.get('dc')?.toLowerCase() === 'latam'
      ? 'LATAM'
      : params.get('dc');

  const [dataCenterRegion, setDataCenterRegion] =
    useState<DataCenterRegion | null>(dataCenter as DataCenterRegion);

  const setDataCenterRegionAndChangeUrl = (
    region: string,
    language?: string
  ): void => {
    addParamToUrl('dc', region);
    addParamToUrlAndRefresh('l', language || selectedLanguage);
    setDataCenterRegion(region as DataCenterRegion);
  };

  const handleSelectLanguage = async (
    languageLocale: string
  ): Promise<void> => {
    addParamToUrl('l', languageLocale);
    await i18n.changeLanguage(languageLocale);
  };

  useEffect(() => {
    handleSelectLanguage(selectedLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (!dataCenter) {
      if (isUS) {
        setDataCenterRegion(DATA_CENTER_REGIONS.USA);
        handleSelectLanguage('en');
        handleIsUsa?.(true);
      } else if (
        country.toLocaleUpperCase().includes('ES') ||
        country.toLocaleUpperCase().includes('IT')
      ) {
        setDataCenterRegion(DATA_CENTER_REGIONS.EUROPE);
      } else {
        setDataCenterRegion(DATA_CENTER_REGIONS.LATAM);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleIsUsa?.(dataCenterRegion === DATA_CENTER_REGIONS.USA);
  }, [dataCenterRegion, handleIsUsa]);

  return {
    dataCenterRegion,
    selectedLanguage,
    setDataCenterRegionAndChangeUrl,
    handleSelectLanguage,
  };
};
